import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import { UserCircle, Mail, Shield, Store, Warehouse, KeyRound, Loader2 } from "lucide-react";

const ROLE_LABEL = {
  owner: "Propriétaire", admin: "Administrateur", manager: "Responsable",
  cashier: "Caissier", seller: "Vendeur", storekeeper: "Magasinier", accountant: "Comptable", auditor: "Auditeur",
};

const Field = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-3 border-b border-[#F1F5F9] last:border-0">
    <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#2563EB] shrink-0"><Icon className="w-4 h-4" /></span>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-semibold">{label}</div>
      <div className="text-sm text-[#16324F] font-medium break-words" data-testid={testId}>{value || "—"}</div>
    </div>
  </div>
);

export default function Account() {
  const { user } = useAuth();
  const { activities, assignedLocations } = useActivity();
  const [sending, setSending] = useState(false);

  const myActivities = (activities || []).filter((a) => a.status !== "archived").map((a) => a.name).join(", ");
  const locations = (assignedLocations || []).join(", ");

  const resetPassword = async () => {
    if (!user.email) return toast.error("Aucune adresse e-mail n'est associée à votre compte. Demandez à votre administrateur.");
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success("Un lien de réinitialisation vient d'être envoyé à votre adresse e-mail.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail, e) || "Envoi impossible"); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="account-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Mon compte</h1>
        <p className="text-sm text-[#64748B] mt-1">Vos informations personnelles et la sécurité de votre compte.</p>
      </div>

      <div className="card p-5">
        <h2 className="text-base font-semibold text-[#16324F] mb-2 flex items-center gap-2"><UserCircle className="w-5 h-5 text-[#2563EB]" /> Identité</h2>
        <Field icon={UserCircle} label="Nom" value={user.name} testId="account-name" />
        <Field icon={Mail} label="Adresse e-mail" value={user.email} testId="account-email" />
        <Field icon={Shield} label="Rôle" value={ROLE_LABEL[user.role] || user.role} testId="account-role" />
        <Field icon={Store} label="Activité(s) affectée(s)" value={myActivities} testId="account-activities" />
        <Field icon={Warehouse} label="Point(s) de vente" value={locations} testId="account-locations" />
        <p className="text-xs text-[#94A3B8] mt-3">Votre identité, votre rôle et votre affectation sont gérés par votre employeur et ne peuvent pas être modifiés ici.</p>
      </div>

      <div className="card p-5">
        <h2 className="text-base font-semibold text-[#16324F] mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#2563EB]" /> Sécurité du compte</h2>
        <p className="text-sm text-[#64748B] mb-4">Vous pouvez réinitialiser votre propre mot de passe. Un lien sécurisé sera envoyé à votre adresse e-mail.</p>
        <button onClick={resetPassword} disabled={sending} className="btn-outline" data-testid="account-reset-password-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Réinitialiser mon mot de passe
        </button>
      </div>
    </div>
  );
}
